import React from "react";
import FlexColumn from "../Layout/FlexColumn/FlexColumn";
import FlexRow from "../Layout/FlexRow/FlexRow";
import Box from "../../primitives/Box/Box";
import { Icon } from "../Icon/Icon";
import {
  Body4MediumTypography,
  Body5MediumTypography,
  Body5RegularTypography,
  Body6RegularTypography,
  Caps1SemiboldTypography,
  Caps2SemiboldTypography,
} from "../../primitives/Typography/typographyVariants";
import {
  PLPCardImage,
  ImageWrapper,
  WishlistIconWrapper,
  TopOverlayRow,
  BottomOverlayRow,
} from "./PLPProductCard.styles";
import { PLPProductCardProps } from "./PLPProductCard.types";
import { PlpSaleTagLabel } from "./PlpSaleTagLabel";

const sanitizePrice = (num?: number): string => {
  if (!num) return "0";
  return Number.parseInt(String(num)).toLocaleString();
};

export const PLPProductCard: React.FC<PLPProductCardProps> = ({
  brandName,
  title,
  price,
  mrp,
  thumbnailUrl,
  fallbackUrl,
  ratingValue,
  ratingCount,
  saleTag,
  itemsLeft,
  isWishlisted = false,
  width = "140px",
  onClick,
  onWishlistClick,
  dataCy,
  hideDiscountPercentage = false,
}) => {
  const discountPercentage =
    mrp !== undefined && price !== undefined && mrp > 0 && mrp > price
      ? Math.round(((mrp - price) / mrp) * 100)
      : 0;

  return (
    <FlexColumn
      backgroundColor="#ffffff"
      borderRadius={4}
      width={width}
      flexShrink={0}
      onClick={onClick}
      cursor={onClick ? "pointer" : "default"}
      data-cy={dataCy}
      overflow="hidden"
    >
      <ImageWrapper>
        <PLPCardImage
          src={thumbnailUrl}
          placeholder={fallbackUrl}
          alt={title}
        />

        <TopOverlayRow>
          {saleTag ? <PlpSaleTagLabel saleTag={saleTag} /> : <Box />}

          {onWishlistClick && (
            <WishlistIconWrapper
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onWishlistClick(e);
              }}
            >
              <Icon
                name={
                  isWishlisted
                    ? "fa-solid fa-bookmark"
                    : "fa-regular fa-bookmark"
                }
                size="14"
                height="14"
                width="14"
                color={isWishlisted ? "#ee4d37" : "#ffffff"}
              />
            </WishlistIconWrapper>
          )}
        </TopOverlayRow>

        {ratingValue !== undefined && (
          <BottomOverlayRow>
            <FlexRow
              alignItems="center"
              backgroundColor="#ffffff"
              borderRadius={5}
              border="1px solid #83e0b8"
              overflow="hidden"
              width="unset"
            >
              <FlexRow
                alignItems="center"
                gap={4}
                px={5}
                py={2}
                backgroundColor="#e6f9f1"
                width="unset"
              >
                <Body5MediumTypography color="#06c270">
                  {ratingValue}
                </Body5MediumTypography>
                <Icon
                  name="fa-solid fa-star"
                  size="10"
                  height="12"
                  width="12"
                  color="#06c270"
                />
              </FlexRow>
              {ratingCount !== undefined && (
                <FlexRow
                  alignItems="center"
                  px={5}
                  backgroundColor="#ffffff"
                  width="unset"
                >
                  <Body6RegularTypography color="#3d3d3d">
                    {ratingCount}
                  </Body6RegularTypography>
                </FlexRow>
              )}
            </FlexRow>
          </BottomOverlayRow>
        )}
      </ImageWrapper>

      <FlexColumn gap={4} p={8} alignItems="flex-start">
        <FlexColumn gap={2} alignItems="flex-start" width="100%">
          <Caps2SemiboldTypography color="#0d0d0d" textTransform="uppercase">
            {brandName}
          </Caps2SemiboldTypography>
          <Body6RegularTypography
            color="#3d3d3d"
            lineClamp={1}
            overflow="hidden"
            textOverflow="ellipsis"
            width="100%"
          >
            {title}
          </Body6RegularTypography>
        </FlexColumn>

        <FlexRow
          gap={8}
          alignItems="center"
          justifyContent="flex-start"
          width="100%"
        >
          <FlexRow
            gap={4}
            alignItems="center"
            justifyContent="flex-start"
            width="unset"
            flex="0 0 auto"
          >
            {mrp !== undefined && mrp > price && (
              <Body5RegularTypography
                textDecoration="line-through"
                color="#8a8a8a"
              >
                &#8377;{sanitizePrice(mrp)}
              </Body5RegularTypography>
            )}
            <Body4MediumTypography color="#0d0d0d">
              &#8377;{sanitizePrice(price)}
            </Body4MediumTypography>
          </FlexRow>
          {discountPercentage > 0 && !hideDiscountPercentage && (
            <Box
              px={4}
              py={2}
              flexShrink={0}
              style={{
                background: "linear-gradient(90deg, #32aa4d 0%, #4fdf90 100%)",
              }}
            >
              <Caps1SemiboldTypography
                color="#ffffff"
                textTransform="uppercase"
              >
                {discountPercentage}% off
              </Caps1SemiboldTypography>
            </Box>
          )}
        </FlexRow>

        {itemsLeft !== undefined && itemsLeft > 0 && (
          <FlexRow
            alignItems="center"
            gap={4}
            px={4}
            py={2}
            backgroundColor="#c2d0f2"
            borderRadius={4}
            width="unset"
          >
            <Icon
              name="fa-solid fa-fire"
              size="10"
              height="12"
              width="12"
              color="#144cc7"
            />
            <Body6RegularTypography color="#144cc7">
              {itemsLeft} items left
            </Body6RegularTypography>
          </FlexRow>
        )}
      </FlexColumn>
    </FlexColumn>
  );
};

export default PLPProductCard;
